import React, { useEffect, useRef, useState } from "react";
import Toggle from "../../components/Toggle";
import BusinessName from "../../components/BusinessName";
import ProfileImg from "../../assets/images/profile-img.png";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getPaymentLink } from "../../services/settings";
import QRCode from "qrcode.react";
import jsPDF from "jspdf";

const PaymentLink = () => {
  const { id } = useParams();
  const [data, setData] = useState({});
  const navigate = useNavigate();
  const qrCanvasRef = useRef(null); // Reference to the QR code canvas
  const [previewImage, setPreviewImage] = useState(null);

  const copyToClipboard = (textToCopy) => {
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        toast.success("Copied to clipboard!");
      })
      .catch((err) => {
        toast.error("Unable to copy to clipboard. Please try again.");
        console.error("Copy to clipboard failed:", err);
      });
  };

  const handleCopyToClipboard = (textToCopy) => {
    copyToClipboard(textToCopy);
  };

  const handleEditLink = () => {
    navigate(`/payments/payment-links/edit-link/${id}`);
  };

  const handleViewTransaction = (id) => {
    navigate(`/payments/payment-links/transaction/${id}`);
  };

  const handleLink = async () => {
    try {
      const res = await getPaymentLink(id);
      setData(res.data);
      if (res.data.image) {
        // Convert binary data to a data URL
        const dataUrl = `data:image/jpeg;base64,${res.data.image}`;
        setPreviewImage(dataUrl);
      }
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  useEffect(() => {
    handleLink();
  }, []);

  const handleReturn = () => {
    navigate("/payments/payment-links");
  };

  function formatDate(dateString) {
    // Create a new Date object from the dateString
    const date = new Date(dateString);

    // Extract the day, month, and year
    const day = String(date.getDate()).padStart(2, "0"); // Ensures 2 digits
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed, so +1
    const year = date.getFullYear();

    // Return the formatted date
    return `${day}/${month}/${year}`;
  }

  const formattedDate = formatDate(data.dateCreated);

  // Function to download the QR code as a PDF
  const downloadQRCode = () => {
    const canvas = qrCanvasRef.current?.querySelector("canvas"); // Find the canvas element
    if (canvas) {
      const imgData = canvas.toDataURL("image/png"); // Convert the canvas to a data URL

      // Create a new PDF document
      const doc = new jsPDF();
      doc.addImage(imgData, "PNG", 10, 10, 180, 180); // Add the QR code image to the PDF
      doc.save("payment-qr-code.pdf"); // Save the PDF with a specific name
    } else {
      toast.error("Unable to generate QR code for download. Please try again.");
    }
  };

  return (
    <div className="xl:w-full w-4/5 xl:px-4 overflow-hidden">
      <div className="xl:flex xl:items-center xl:justify-between">
        <h1 className="text-2xl font-bold text-navy-blue">Payments - Links</h1>
        <Toggle />
      </div>

      <section>
        <div className="border border-gray-200 md:max-w-full max-w-72 rounded-t-[10px] p-[1rem] pl-7 flex justify-between items-center mb-8">
          <p className="flex gap-2 items-center text-green-600 text-xl font-semibold">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_4474_11607)">
                <path
                  d="M19 0H5.00002C2.23997 0.00332812 0.00332812 2.23997 0 5.00002V19C0.00332812 21.76 2.23997 23.9967 5.00002 24H19C21.76 23.9967 23.9967 21.76 24 19V5.00002C23.9967 2.23997 21.76 0.00332812 19 0ZM20 8.079L10.746 17.333C9.96511 18.1142 8.69878 18.1145 7.91756 17.3336C7.91738 17.3334 7.91719 17.3332 7.917 17.333L3.99998 13.417C3.60867 13.0257 3.60867 12.3913 3.99998 12C4.3913 11.6088 5.0257 11.6087 5.41697 12L9.33295 15.916L18.588 6.66202C18.9793 6.27211 19.6125 6.27323 20.0025 6.6645C20.3924 7.05581 20.3913 7.68909 20 8.079Z"
                  fill="#20A464"
                />
              </g>
              <defs>
                <clipPath id="clip0_4474_11607">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            Your Payment Link is ready to go
          </p>
          <span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.5 6.75L9 11.25L13.5 6.75"
                stroke="#464255"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </section>

      <div className="flex flex-col mb-6 gap-2">
        <div className="font-bold text-lg md:text-4xl">
          Payment Link for <BusinessName />
        </div>

        <div className="font-normal text-sm md:max-w-full max-w-60">
          Copy and share to start accepting payments via this link.
        </div>
      </div>

      <div className=" xl:flex items-start xl:items-center gap-2">
        <button className="bg-gray-100 p-2 py-3 rounded-md md:w-4/5 xl:w-[442px]">
          {data.link && data.link.length > 50
            ? `${data.link.slice(0, 50)}...`
            : data.link}
        </button>

        <button
          className="flex items-center bg-primary text-white gap-2 py-2 px-4 md:mt-0 mt-4 rounded-md"
          onClick={() => handleCopyToClipboard(data.link)}
        >
          <span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_4474_11623)">
                <path
                  d="M9.8125 13H3.5625C2.734 12.999 1.93972 12.6694 1.35389 12.0836C0.768051 11.4978 0.438492 10.7035 0.4375 9.875L0.4375 3.625C0.438492 2.7965 0.768051 2.00222 1.35389 1.41639C1.93972 0.830551 2.734 0.500992 3.5625 0.5L9.8125 0.5C10.641 0.500992 11.4353 0.830551 12.0211 1.41639C12.6069 2.00222 12.9365 2.7965 12.9375 3.625V9.875C12.9365 10.7035 12.6069 11.4978 12.0211 12.0836C11.4353 12.6694 10.641 12.999 9.8125 13ZM15.4375 12.375V4.25C15.4375 4.08424 15.3717 3.92527 15.2544 3.80806C15.1372 3.69085 14.9783 3.625 14.8125 3.625C14.6467 3.625 14.4878 3.69085 14.3706 3.80806C14.2533 3.92527 14.1875 4.08424 14.1875 4.25V12.375C14.1875 12.8725 13.9895 13.3494 13.6393 13.6997C13.289 14.05 12.8125 14.248 12.3125 14.25H5.1875C5.02174 14.25 4.86277 14.3158 4.74556 14.4331C4.62835 14.5503 4.5625 14.7093 4.5625 14.875C4.5625 15.0408 4.62835 15.1997 4.74556 15.3169C4.86277 15.4342 5.02174 15.5 5.1875 15.5H12.3125C13.1407 15.4987 13.9339 15.1688 14.519 14.5838C15.104 13.9987 15.4338 13.2054 15.4375 12.375Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_4474_11623">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </span>

          <span>Copy link</span>
        </button>

        <section className="flex flex-col gap-2">
          <div
            ref={qrCanvasRef}
            className=" hidden justify-center mt-2 xl:mt-0"
          >
            {data.link && <QRCode value={data.link} size={100} level={"H"} />}
          </div>

          <button
            className="flex items-center justify-center md:mt-0 mt-4 w-4/5 xl:w-[180px] gap-1 border p-2 rounded-md font-normal border-black"
            onClick={downloadQRCode}
          >
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_4475_11661)">
                <path
                  d="M5.9375 4.5625V6.4375H4.0625V4.5625H5.9375ZM6.875 3.625H3.125V7.375H6.875V3.625ZM5.9375 9.5625V11.4375H4.0625V9.5625H5.9375ZM6.875 8.625H3.125V12.375H6.875V8.625ZM10.9375 4.5625V6.4375H9.0625V4.5625H10.9375ZM11.875 3.625H8.125V7.375H11.875V3.625ZM8.125 8.625H9.0625V9.5625H8.125V8.625ZM9.0625 9.5625H10V10.5H9.0625V9.5625ZM10 8.625H10.9375V9.5625H10V8.625ZM8.125 10.5H9.0625V11.4375H8.125V10.5ZM9.0625 11.4375H10V12.375H9.0625V11.4375ZM10 10.5H10.9375V11.4375H10V10.5ZM10.9375 9.5625H11.875V10.5H10.9375V9.5625ZM10.9375 11.4375H11.875V12.375H10.9375V11.4375ZM13.125 4.875C12.7812 4.875 12.5 4.59375 12.5 4.25V3H11.25C10.9062 3 10.625 2.71875 10.625 2.375C10.625 2.03125 10.9062 1.75 11.25 1.75H13.125C13.4688 1.75 13.75 2.03125 13.75 2.375V4.25C13.75 4.59375 13.4688 4.875 13.125 4.875ZM13.75 13.625V11.75C13.75 11.4062 13.4688 11.125 13.125 11.125C12.7812 11.125 12.5 11.4062 12.5 11.75V13H11.25C10.9062 13 10.625 13.2812 10.625 13.625C10.625 13.9688 10.9062 14.25 11.25 14.25H13.125C13.4688 14.25 13.75 13.9688 13.75 13.625ZM1.875 14.25H3.75C4.09375 14.25 4.375 13.9688 4.375 13.625C4.375 13.2812 4.09375 13 3.75 13H2.5V11.75C2.5 11.4062 2.21875 11.125 1.875 11.125C1.53125 11.125 1.25 11.4062 1.25 11.75V13.625C1.25 13.9688 1.53125 14.25 1.875 14.25ZM1.25 2.375V4.25C1.25 4.59375 1.53125 4.875 1.875 4.875C2.21875 4.875 2.5 4.59375 2.5 4.25V3H3.75C4.09375 3 4.375 2.71875 4.375 2.375C4.375 2.03125 4.09375 1.75 3.75 1.75H1.875C1.53125 1.75 1.25 2.03125 1.25 2.375Z"
                  fill="#323232"
                />
              </g>
              <defs>
                <clipPath id="clip0_4475_11661">
                  <rect
                    width="15"
                    height="15"
                    fill="white"
                    transform="translate(0 0.5)"
                  />
                </clipPath>
              </defs>
            </svg>
            Download QR Code
          </button>
        </section>

        <button
          className=" rounded-md border p-2 border-black py-2"
          onClick={() => {
            handleViewTransaction(id);
          }}
        >
          View Transactions
          {/* <svg
            width="37"
            height="15"
            viewBox="0 0 37 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_4474_11646)">
              <path
                d="M12.6411 8.75003C13.3315 8.75003 13.8911 8.19038 13.8911 7.50001C13.8911 6.80965 13.3315 6.25 12.6411 6.25C11.9508 6.25 11.3911 6.80965 11.3911 7.50001C11.3911 8.19038 11.9508 8.75003 12.6411 8.75003Z"
                fill="black"
              />
              <path
                d="M18.8911 8.75003C19.5815 8.75003 20.1411 8.19038 20.1411 7.50001C20.1411 6.80965 19.5815 6.25 18.8911 6.25C18.2008 6.25 17.6411 6.80965 17.6411 7.50001C17.6411 8.19038 18.2008 8.75003 18.8911 8.75003Z"
                fill="black"
              />
              <path
                d="M25.1411 8.75003C25.8315 8.75003 26.3912 8.19038 26.3912 7.50001C26.3912 6.80965 25.8315 6.25 25.1411 6.25C24.4508 6.25 23.8911 6.80965 23.8911 7.50001C23.8911 8.19038 24.4508 8.75003 25.1411 8.75003Z"
                fill="black"
              />
            </g>
            <defs>
              <clipPath id="clip0_4474_11646">
                <rect
                  width="15"
                  height="15"
                  fill="white"
                  transform="translate(11.3911)"
                />
              </clipPath>
            </defs>
          </svg> */}
        </button>
      </div>

      <section className="mt-8">
        <div className="border-b xl:pb-2 mb-8 w-4/5 md:w-full">Details</div>
        <div className="xl:flex xl:flex-row flex flex-col-reverse gap-6">
          <div className="md:w-1/2 p-2 px-4 gap-3 flex flex-col">
            {/* <div className='flex'>
                            <div>Status</div>
                            <svg
                                width="818" height="22" viewBox="0 0 818 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="790" y="0.5" width="28" height="15" rx="7.5" fill="#20A464" />
                                <rect x="804.99" y="2.49023" width="11" height="11" rx="5.5" fill="white" />
                            </svg>
                        </div> */}

            <div className="flex justify-between">
              <div>Date Created</div>
              <div>{formattedDate}</div>
            </div>

            <div className="flex justify-between">
              <div>Currency</div>
              <div>{data.currency}</div>
            </div>

            <div className="flex justify-between">
              <div>Payment Link Type</div>
              <div>{data.paymentLinkType}</div>
            </div>

            <div className="flex justify-between">
              <div>Amount</div>
              <div>{data.amount}</div>
            </div>

            <div className="mt-12 flex gap-4 justify-center">
              <button
                className="md:w-[142px] w-full text-primary border border-primary p-2 rounded-md"
                onClick={handleReturn}
              >
                Back
              </button>
              <button
                className="md:w-[272px] w-full bg-primary text-white p-2 rounded-md"
                onClick={handleEditLink}
              >
                Edit Link
              </button>
            </div>
          </div>

          <div className="w-1/2 flex flex-col">
            {previewImage ? (
              <img
                src={previewImage}
                alt="Preview"
                className="w-[114px] h-[114px] rounded-full"
              />
            ) : (
              <div className="w-[114px] h-[114px] bg-gray-500/15 rounded-full relative">
                <img
                  src={ProfileImg}
                  alt="preview"
                  className="w-[114px] h-[114px] rounded-full"
                />
              </div>
            )}

            <p className="text-sm text-center w-[212px] h-[45px] mt-3 font-normal text-darker-grey">
              *The uploaded image must be 500px wide and 500px long
            </p>

            {/* <div className='flex pb-7 gap-2 flex-col items-center w-[198px] text-center'>
                            <img src={data.image} alt=''
                                className="w-[114px] h-[114px]" >
                            </img>

                            * The uploaded image must be 500px wide and 500px long
                        </div> */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PaymentLink;
